import type { MaterialSymbol } from "@qw/ui";

/**
 * Gmail's `subject:` and `from:` in QWIB's search box: what the operators are, how a typed term
 * splits into them, and how they read back. One module so adding a third is one file.
 *
 * It lives in the front end deliberately. `GetConversationsInput` takes structured dimensions —
 * `search`, `subjectSearch`, `senderSearch` — so Gmail's grammar stays a UI affordance, and the next
 * caller of that search (CRM, the web app, a script) is not bound to it.
 *
 * The grammar is deliberately tiny: two prefixes, one value each, no quoting, no negation, no `OR`.
 * Everything beyond these two needs a server dimension that does not exist, and answering syntax we
 * cannot honour is the defect this replaces — `websearch_to_tsquery('english', 'subject:renewal')`
 * compiles to `'subject' & 'renew'`, and because every quoted reply chain contains the literal word
 * "Subject:", `subject:renewal` returned 1,606 conversations where the 379 real subject matches were
 * wanted. Confidently, with no error and no empty state.
 *
 * So an unrecognised prefix is left as literal text rather than swallowed: `bcc:legal@acme.com`
 * searches for that string, which is exactly what it did before this parser existed. Silently
 * dropping it would be the same class of lie in the other direction.
 */

/** The prefixes the box understands, spelled as a broker types them. */
const SEARCH_OPERATORS = ["subject", "from"] as const;

export type SearchOperator = (typeof SEARCH_OPERATORS)[number];

/**
 * How each operator reads, in one table so a chip and the pill offering it cannot end up naming
 * different dimensions. `label` is the operator's own word, because that is the vocabulary the
 * broker just typed; `dimension` is the same thing said aloud, and it differs for `from:` on
 * purpose — "Remove from filter: sarah" parses three ways read out, and "sender" is what the server
 * calls it anyway.
 */
export const SEARCH_OPERATOR_COPY: Record<SearchOperator, { icon: MaterialSymbol; label: string; dimension: string }> =
  {
    subject: { icon: "subject", label: "Subject", dimension: "subject" },
    from: { icon: "person", label: "From", dimension: "sender" },
  };

/**
 * A typed search, split into what the server takes. The field names are `GetConversationsInput`'s on
 * purpose — callers hand this straight to the query — but the type is local so a text parser does
 * not depend on the generated schema.
 */
export interface ParsedSearchQuery {
  /** Everything with no operator on it, matched across every dimension the way it always was. */
  search: string;
  subjectSearch?: string;
  senderSearch?: string;
}

// `[a-z]+` rather than `\w+` is what keeps a time out of the grammar: `10:30` has no letter prefix
// to match, so it stays a literal term. The value is whatever follows the *first* colon, so
// `subject:renewal:2026` scopes the whole of `renewal:2026` to the subject.
const OPERATOR_TOKEN = /^([a-z]+):(.*)$/i;

const isSearchOperator = (prefix: string): prefix is SearchOperator =>
  SEARCH_OPERATORS.includes(prefix as SearchOperator);

/**
 * Splits a typed term into its unscoped half and its scoped dimensions.
 *
 * One value per dimension, last one typed winning, because the server takes one term per dimension
 * — retyping `subject:` is how a broker corrects it rather than a way to AND two subjects.
 */
export const parseSearchQuery = (term: string): ParsedSearchQuery => {
  const unscoped: string[] = [];
  const scoped: Partial<Record<SearchOperator, string>> = {};

  for (const token of term.split(/\s+/).filter(Boolean)) {
    const match = OPERATOR_TOKEN.exec(token);
    const prefix = match?.[1].toLowerCase();

    if (!prefix || !isSearchOperator(prefix)) {
      unscoped.push(token);
      continue;
    }

    // A prefix with nothing after it is the operator half-typed — which is precisely what the offer
    // pill puts in the box. It narrows nothing, and it is not searchable text either: letting it
    // fall through to `search` would scan the mailbox for the literal string "subject:" the moment
    // the pill was clicked, which is the noise this parser exists to remove.
    if (match?.[2]) {
      scoped[prefix] = match[2];
    }
  }

  return { search: unscoped.join(" "), subjectSearch: scoped.subject, senderSearch: scoped.from };
};

/**
 * How much search text a parsed query actually carries, across every dimension.
 *
 * The prefixes are grammar rather than text to match, so they do not count — which is what lets one
 * number answer both questions the box asks. Is there anything here to run at all (`> 0`, so a bare
 * `subject:` is not a search), and is it long enough to be worth a server scan
 * (`MINIMUM_SUGGESTION_TERM_LENGTH`, whose whole point is not paying for a one-letter term).
 */
export const searchTextLength = ({ search, subjectSearch, senderSearch }: ParsedSearchQuery) =>
  search.length + (subjectSearch?.length ?? 0) + (senderSearch?.length ?? 0);
